import React, {useEffect, useCallback, useState} from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AppHeader } from "./containers/Header/AppHeader";
import { AppFooter } from "./containers/Footer/AppFooter";
import SubscriptionsPage from "./containers/SubscriptionsPage";
import { StorePage } from "./containers/StorePage";
import { RegistrationPage } from "./containers/RegistrationPage";
import { Alert, useCommonTranslations, useErrorContext, useRegionContext } from "tds_shared_ui";
import {ProfilePage} from "./containers/ProfilePage";
import {KeycodePage} from "./containers/KeycodePage";
import {MyOrdersPage} from "./containers/MyOrdersPage";
import {CheckoutPage} from "./containers/CheckoutPage";
import {ShoppingCartPage} from "./containers/ShoppingCartPage";
import {HelpPage} from "./containers/HelpPage";
import {TermsAndConditionsPage} from "./containers/TermsAndConditionsPage";
import {EulaPage} from "./containers/EulaPage";
import {RpoLookupPage} from "./containers/RpoLookupPage";
import {HowToBuyPage} from "./containers/HowToBuyPage";
import NotFound from "./containers/NotFound";
import {
  CheckoutTranslationsProvider,
  ContactTranslationsProvider,
  KeyCodeTranslationsProvider,
  SubscriptionsTranslationsProvider,
  UserTranslationsProvider,
} from "./context";
import {AuthService, TermsService} from "./services";
import PropTypes from "prop-types";
import {ContactUsPage} from "./containers/ContactUsPage";
import LogoutPage from "./containers/LogoutPage";
import {useUserContext} from "./context/UserContext";
import {useLinksContext} from "./context/LinksContext";
import {TermsAndConditions} from "./components/UI";

const AppRouter = ({
    onLanguageChange,
    onRegionRefresh,
                   }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, regionData } = useRegionContext();
  const { clearError, alert, alertRef } = useErrorContext();
  const { user, setUser, userAgreed, setUserAgreed } = useUserContext();
  const { footerLinks } = useLinksContext();
  const [termsData, setTermsData] = useState();
  const [showTerms, setShowTerms] = useState(false);

  const handleLanguageChange = useCallback((langCode) => {
    if (onLanguageChange) onLanguageChange(langCode);
  }, [onLanguageChange]);

    useEffect(() => {
        if (user && !userAgreed) {
            setShowTerms(true);
        } else if (user && userAgreed){
            setShowTerms(false);
        }
    }, [user, userAgreed]);

  useEffect(() => {
    handleStaticContent();
  }, [locale]);

  useEffect(() => {
    AuthService.handleUnauthorizedErrors((error) => {
      setUser(null);
      const requestUrl = error.config?.url ?? "";
      if (
        !requestUrl.includes("accessSPSCal") &&
        !requestUrl.includes("accessRPOLookup")
      ) {
        navigate("/subscriptions", { replace: true });
      }
    });
  }, [navigate, setUser]);

  // Clear error on route change
  useEffect(() => {
    clearError();
  }, [location.pathname, clearError]);

  function handleStaticContent() {
    const tdstermsconditions = "tdstermsconditions";
    TermsService.getTerms(tdstermsconditions, locale)
        .then((data) => {
            setTermsData(data.content);
        })
        .catch(() => {});
  }
  const handleTermsSuccess = () => {
    setUserAgreed(true);
    setShowTerms(false);
  };

  const handleTermsCancel = () => {
    setShowTerms(false);
    navigate("/logout");
  };

  if (user && !userAgreed && showTerms) {
    return (
        <TermsAndConditions
            onSuccess={handleTermsSuccess}
            open={showTerms}
            onCancel={handleTermsCancel}
            text={
                <div
                    dangerouslySetInnerHTML={{
                        __html: termsData,
                    }}
                ></div>
            }
        />
    );
  }
  return (
    <div className="App">
      <AppHeader
          locale={locale}
          onLocaleChange={handleLanguageChange}
          onRegionRefresh={onRegionRefresh}
      />

      <div className="container">
        {alert.display &&
            <Alert
                ref={alertRef}
                className={"alert--default alert tdsAlert tds-alert"}
                dismissible={true}
                onDismiss={clearError}
                type={alert ? alert.type : "info"}
                children={alert ? alert.message : ""}
                dismissComponent={
                  <button type="button" onClick={clearError}>
                    <i className="fa fa-times"></i>
                  </button>
                }
            />
        }
        <Routes>
          <Route path="/" element={<Navigate to="/subscriptions" replace={true} />} />
          <Route
            path="/subscriptions"
            element={
              <SubscriptionsTranslationsProvider locale={locale}>
                <SubscriptionsPage locale={locale} />
              </SubscriptionsTranslationsProvider>
            }
          />

          <Route
            path="/login"
            element={<Navigate to="/subscriptions" replace={true} />}
          />

          <Route path="/keycodes" element={
            <RegionalRoute
              locale={locale}
              render={(props) => (
                <KeyCodeTranslationsProvider locale={locale}>
                  <KeycodePage {...props} locale={locale} />
                </KeyCodeTranslationsProvider>
              )}
            />
          } />

          <Route path="/myorders" element={
            <PrivateRoute
              locale={locale}
              render={(props) => (
                <KeyCodeTranslationsProvider locale={locale}>
                  <MyOrdersPage {...props} />
                </KeyCodeTranslationsProvider>
              )}
            />
          } />
          <Route path="/store" element={<StoreRoute />} />
          <Route path="/shoppingcart" element={<ShoppingCartPage locale={locale} />} />
          <Route path="/checkout" element={
            <CheckoutTranslationsProvider locale={locale}>
              <CheckoutPage />
            </CheckoutTranslationsProvider>
          } />
          <Route path="/help" element={<HelpPage locale={locale} />} />
          <Route path="/terms" element={<TermsAndConditionsPage locale={locale} />} />
          <Route path="/eula" element={<EulaPage locale={locale} />} />
          <Route path="/registration" element={<RegistrationRoute locale={locale} />} />
          <Route path="/profile" element={
            <PrivateRoute
              locale={locale}
              render={(props) => (
                <UserTranslationsProvider locale={locale}>
                  <ProfilePage {...props} />
                </UserTranslationsProvider>
              )}
            />
          } />
          <Route path="/contactus" element={
            <ContactTranslationsProvider locale={locale}>
              <ContactUsPage regionData={regionData} />
            </ContactTranslationsProvider>
          } />
          <Route path="/rpolookup" element={<RpoLookupPage />} />
          <Route path="/howtobuy" element={<HowToBuyPage locale={locale} />} />
          <Route path="/logout" element={<LogoutPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      <AppFooter footerLinks={footerLinks} />
    </div>
  );
};

AppRouter.propTypes = {
  onLanguageChange: PropTypes.func,
  onRegionRefresh: PropTypes.func,
};

const StoreRoute = () => {
  const { regionData } = useRegionContext();
  const { translate } = useCommonTranslations();

  return regionData !== null && regionData.regionConfigs.storeLink ? (
      <StorePage />
  ) : (
      <Alert type="error">
        {translate("ERROR_REGION_UNAUTHORIZED_PAGE")}
      </Alert>
  );
};

const RegistrationRoute = ({ locale }) => {
  const { user } = useUserContext();

  return user !== null ? (
      <Navigate to="/" />
  ) : (
      <UserTranslationsProvider locale={locale}>
        <RegistrationPage locale={locale} />
      </UserTranslationsProvider>
  );
};

const PrivateRoute = ({ render: componentRender, ...rest }) => {
  const { user } = useUserContext();
  const { translate } = useCommonTranslations();

  if (user !== null) {
    return componentRender(rest);
  }

  return (
      <Alert type="error">
        {translate("ERROR_UNAUTHORIZED_PAGE")}
      </Alert>
  );
};

PrivateRoute.propTypes = {
  render: PropTypes.func.isRequired,
  locale: PropTypes.string,
};

const RegionalRoute = ({ render: componentRender, ...rest }) => {

  const { regionConfig } = useRegionContext();
  const { translate } = useCommonTranslations();
  const allowed = regionConfig.features.keycodeAvailable
  return allowed ? (
      componentRender(rest)
  ) : (
      <Alert type="error">
        {translate("ERROR_REGION_UNAUTHORIZED_PAGE")}
      </Alert>
  );
};

RegionalRoute.propTypes = {
  render: PropTypes.func.isRequired,
  locale: PropTypes.string,
};

export default AppRouter;